import React from 'react';
import { usePermissions } from '../../contexts/PermissionsContext';
import { Lock, Shield, AlertCircle } from 'lucide-react';

export const ProtectedComponent = ({ 
  permission, 
  permissions, 
  requireAll, 
  children,
  fallback = null,
  showMessage = false 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && requireAll) {
    hasAccess = hasAllPermissions(permissions);
  } else if (permissions) {
    hasAccess = hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    if (showMessage) {
      return (
        <div className="flex flex-col items-center justify-center p-12 bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl border-2 border-yellow-500/20 shadow-2xl backdrop-blur-sm">
          {/* Emoji كبير ومبهج */}
          <div className="text-8xl mb-6 animate-bounce">
            🔒
          </div>
          
          {/* عنوان رئيسي */}
          <h3 className="text-2xl font-bold text-yellow-400 mb-3 text-center">
            عفواً! 🌟
          </h3>
          
          {/* الرسالة */}
          <p className="text-lg text-gray-300 text-center mb-4 max-w-md leading-relaxed">
            هذا القسم يحتاج إلى صلاحيات خاصة للوصول إليه
          </p>
          
          {/* رسالة تحفيزية */}
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
            <Shield className="w-5 h-5 text-yellow-400" />
            <p className="text-sm text-yellow-300">
              يمكنك التواصل مع المسؤول لطلب الصلاحيات 💬
            </p>
          </div>
        </div>
      );
    }
    return fallback;
  }

  return <>{children}</>;
};

export const ProtectedButton = ({ 
  permission, 
  permissions,
  requireAll,
  children, 
  className = '',
  disabledClassName = 'opacity-50 cursor-not-allowed',
  showTooltip = true,
  ...props 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && requireAll) {
    hasAccess = hasAllPermissions(permissions);
  } else if (permissions) {
    hasAccess = hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    return (
      <button
        {...props}
        disabled
        className={`${className} ${disabledClassName} relative group`}
        title={showTooltip ? "يحتاج صلاحيات خاصة 🔐" : ""}
      >
        {children}
        {showTooltip && (
          <span className="absolute -top-12 left-1/2 transform -translate-x-1/2 px-3 py-2 bg-gray-900 text-yellow-300 text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-yellow-500/30 shadow-lg">
            🔒 يحتاج صلاحيات خاصة
            <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 border-r border-b border-yellow-500/30"></span>
          </span>
        )}
      </button>
    );
  }

  return (
    <button {...props} className={className}>
      {children}
    </button>
  );
};

export const ProtectedSection = ({ permission, permissions, requireAll, children }) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && requireAll) {
    hasAccess = hasAllPermissions(permissions);
  } else if (permissions) {
    hasAccess = hasAnyPermission(permissions);
  }

  if (!hasAccess) return null;

  return <>{children}</>;
};